import { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CV_ANALYZER_API_BASE_URL } from "../../constants/config"
import { InboxOutlined, FileTextOutlined } from "@ant-design/icons";
import { App as AntdApp, Upload, Slider, Button, Spin, Input, Tabs } from "antd";
import ResumeResultsTable from "../../Components/TableLayout/TableLayout";
import styles from "./AnalyzeResumes.css";


const { Dragger } = Upload;
const { TextArea } = Input;

const AnalyzeResumes = () => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const [jdFile, setJdFile] = useState(null);
  const [jdText, setJdText] = useState("");
  const [resumeFileList, setResumeFileList] = useState([]);
  const [threshold, setThreshold] = useState(70);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeJdTab, setActiveJdTab] = useState("upload");
  const { message } = AntdApp.useApp();

  const readFileContent = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      setJdText(content || "");
    };
    reader.readAsText(file);
  };

  const handleJdFileUpload = (file) => {
    if (file.type !== "text/plain") {
      message.error("Only TXT file is allowed!");
      return Upload.LIST_IGNORE;
    }
    setJdFile(file);
    readFileContent(file);
    setActiveJdTab("text");
    return false;
  };

  const handleAnalyze = async () => {
    if ((!jdFile && !jdText.trim()) || resumeFileList.length === 0) {
      message.error("Please provide a job description and upload at least one resume.");
      return;
    }

    setLoading(true);
    setAnalysisResults([]);

    const formData = new FormData();

    if (jdText.trim()) {
      const jdBlob = new Blob([jdText], { type: "text/plain" });
      const jdFileFromText = new File([jdBlob], jdFile?.name || "job_description.txt", {
        type: "text/plain",
      });
      formData.append("jd_file", jdFileFromText);
    } else if (jdFile) {
      formData.append("jd_file", jdFile);
    }

    resumeFileList.forEach((fileObj) => {
      if (fileObj.originFileObj) {
        formData.append("resume_files", fileObj.originFileObj);
      }
    });

    formData.append("threshold_score", String(threshold));

    try {
      const response = await fetch(
        `${CV_ANALYZER_API_BASE_URL}/${auth?.userId}/resumes/analyze_resumes`,    // Replace with backend server endpoint in production
        {
          method: "POST",
          body: formData,
          headers: {
            Accept: "text/plain",
            Authorization: `Bearer ${auth?.token}`,
          }
        }
      );

      if (response.status === 401) {
        const errorData = await response.json();
        message.error(
          errorData.detail === "Token expired"
            ? "Session expired. Redirecting..."
            : "Not authenticated!"
        );
        navigate("/");
        return;
      }

      if (response.status === 403) {
        message.error("Access denied.");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to analyze resumes.");
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("Failed to read response stream.");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const delimiter = "|||BATCH_END|||";
        const parts = buffer.split(delimiter);

        for (let i = 0; i < parts.length - 1; i++) {
          const jsonPart = parts[i];
          try {
            const data = JSON.parse(jsonPart);
            if (data.batch_results && data.batch_results.length > 0) {
              setAnalysisResults((prevResults) => [...prevResults, ...data.batch_results]);
            }
          } catch (err) {
            console.warn("⚠️ Error parsing JSON batch:", err, jsonPart);
          }
        }

        buffer = parts[parts.length - 1] || "";
      }

      message.success("Analysis completed!");
    } catch (error) {
      console.error("Error analyzing resumes:", error);
      message.error("Error analyzing resumes.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Job Description</h2>

      <Tabs
        activeKey={activeJdTab}
        onChange={setActiveJdTab}
        className="mb-4"
        items={[
          {
            key: "upload",
            label: "Upload File",
            children: (
              <Dragger
                accept=".txt"
                multiple={false}
                fileList={[]}
                beforeUpload={handleJdFileUpload}
                className={styles.jdFileText}
              >
                <p className="ant-upload-drag-icon">
                  <FileTextOutlined />
                </p>
                <p className="ant-upload-text">Click or drag JD file here</p>
                <p className="ant-upload-hint">Only TXT files are supported</p>
              </Dragger>
            ),
          },
          {
            key: "text",
            label: "Enter Text",
            children: (
              <TextArea
                rows={10}
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
                placeholder="Paste your job description here..."
                className={styles.textarea}
              />
            ),
          },
        ]}
      />

      {jdFile && <p className="mt-2 text-green-600">JD File: {jdFile.name}</p>}

      <div style={{ marginTop: "32px" }} />

      <h2 className={styles.heading}>Upload Resumes</h2>
      <Dragger
        accept=".pdf"
        multiple
        directory
        fileList={resumeFileList}
        onChange={({ fileList }) => setResumeFileList(fileList)}
        beforeUpload={(file) => {
          if (file.type !== "application/pdf") {
            message.error("Only PDF files are allowed!");
            return Upload.LIST_IGNORE;
          }
          return false;
        }}
      >
        <p className="ant-upload-drag-icon text-[#E84C66]">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or Drag Resume Folder here</p>
        <p className="ant-upload-hint">Only PDF files are supported</p>
      </Dragger>
      {resumeFileList.length > 0 && (
        <p className={styles.resumeCount}>{resumeFileList.length} resumes uploaded.</p>
      )}

      <div style={{ marginTop: "32px" }} />

      <div style={{ marginTop: "32px" }}>
        <label className={styles.thresholdLabel}>Threshold: {threshold}%</label>
        <Slider min={0} max={100} value={threshold} onChange={setThreshold} />
      </div>

      <div style={{ marginTop: "32px" }} />

      <Button type="primary" className={styles.analyzeButton} onClick={handleAnalyze} disabled={loading}>
        Analyze
      </Button>

      {loading && (
        <div className="spinner-container">
          <Spin size="large" />
        </div>
      )}

      {analysisResults.length > 0 && (
        <div style={{ marginTop: "32px" }}>
          <ResumeResultsTable data={analysisResults} />
        </div>
      )}
    </div>
  );
};

export default AnalyzeResumes;
